import React from "react";
import styled from "@emotion/styled";

/**
 * 버튼 컴포넌트의 props 타입 정의
 */
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 버튼 내용 */
  children: React.ReactNode;
  /** 버튼 색상 테마 */
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "black"
    | "white"
    | "outline"
    | "disabled"
    | "secondaryLight"
    | "error"
    | "secondaryPrimary"
    | "secondaryError"
    | "primaryWhite";
  /** 버튼 크기 */
  size?: "xs" | "sm" | "md" | "mmd" | "xxl" | "icon";
  /** 너비 100% 설정 */
  fullWidth?: boolean;
  /** 커스텀 패딩 값 */
  padding?: string;
  /** CSS 클래스명 */
  className?: string;
  /** 클릭 이벤트 핸들러 */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** 비활성화 상태 */
  disabled?: boolean;
}

/**
 * StyledButton의 props 타입 정의
 */
interface StyledButtonProps {
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  fullWidth?: boolean;
  padding?: string;
}

const StyledButton = styled("button", {
  shouldForwardProp: (prop: string) => !["variant", "size", "fullWidth", "padding"].includes(prop),
})<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
  }

  ${({ variant }) => {
    switch (variant) {
      case "default":
        return `
          background-color: #323639;
          color: #fff;
          &:hover:not(:disabled) {
            background-color: #4a4d50;
          }
        `;
      case "primary":
        return `
          background-color: #047BBC;
          color: #fff;
          &:hover:not(:disabled) {
            background-color: #036ba3;
          }
        `;
      case "secondary":
        return `
          background-color: #06C167;
          color: #fff;
          &:hover:not(:disabled) {
            background-color: #05a855;
          }
        `;
      case "disabled":
        return `
          background-color: #EBEDEE;
          border: 1px solid #B2B3B4;
          color: #717272;
        `;
      case "outline":
        return `
          background-color: #fff;
          color: #323538;
          border: 1px solid #d0d5dd;
        `;
      case "black":
        return `
          background-color: #1A1A1A;
          color: #fff;
          &:hover:not(:disabled) {
            background-color: #2a2a2a;
          }
          &:disabled {
            border: 1px solid #C1C2C3;
            background: #E1E3E4;
            color: #717272;
          }
        `;
      case "white":
        return `
          background-color: #fff;
          color: #6B7884;
          border: 1px solid #B2B3B4;
        `;
      case "secondaryLight":
        return `
          background-color: #E4E4E7;
          color: #3f3f46;
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
          font-weight: 600;
          &:hover:not(:disabled) {
            background-color: #E4E4E7;
          }
          &:disabled {
            background-color: #d4d4d5;
            color: #70707B;
          }
        `;
      case "secondaryPrimary":
        return `
          background-color: #ECECED;
          color: #2C67F5;
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
          font-weight: 600;
          &:hover:not(:disabled) {
            background-color: #d4d4d5;
          }
        `;
      case "secondaryError":
        return `
          background-color: #ECECED;
          color: #DD104E;
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
          font-weight: 600;
          &:hover:not(:disabled) {
            background-color: #d4d4d5;
          }
        `;
      case "error":
        return `
        background-color: #DD104E;
        color: #FFF;
        border: none;
        border-radius: 8px;
        box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
        font-weight: 600;
        &:hover:not(:disabled) {
          background-color: #c01045;
        }
      `;
      case "primaryWhite":
        return `
          background-color: #2C67F5;
          color: #FFF;
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
          font-weight: 600;
          &:hover:not(:disabled) {
            background-color: #2353c0;
          }
        `;
      default:
        return `
          background-color: #323639;
          color: #fff;
          &:hover:not(:disabled) {
            background-color: #4a4d50;
          }
        `;
    }
  }}

  /* Size styles */
  ${({ size, padding }) => {
    let sizeStyles = "";

    switch (size) {
      case "xs":
        sizeStyles = `font-size: 14px;`;
        break;
      case "sm":
        sizeStyles = `font-size: 16px;`;
        break;
      case "mmd":
        sizeStyles = `font-size: 17px;`;
        break;
      case "xxl":
        sizeStyles = `font-size: 28px;`;
        break;
      case "icon":
        sizeStyles = `font-size: 16px;`;
        break;
      default:
        sizeStyles = `font-size: 16px;`;
        break;
    }

    let paddingStyles = "";
    if (padding) {
      paddingStyles = `padding: ${padding};`;
    } else {
      switch (size) {
        case "sm":
          paddingStyles = `padding: 14px;`;
          break;
        case "mmd":
          paddingStyles = `padding: 10px;`;
          break;
        case "xxl":
          paddingStyles = `padding: 23px 21px;`;
          break;
        case "icon":
          paddingStyles = `padding: 8px;`;
          break;
        default:
          paddingStyles = `padding: 8px 12px;`;
          break;
      }
    }

    return sizeStyles + paddingStyles;
  }}
  
  ${({ fullWidth }) => (fullWidth ? "width: 100%;" : "width: fit-content;")}
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, size = "md", className, fullWidth = true, disabled, onClick, variant = "default", padding, ...props },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        className={className}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        padding={padding}
        onClick={onClick}
        {...props}
      >
        {children}
      </StyledButton>
    );
  }
);

Button.displayName = "Button";
